import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertSpg,
  AsyncCallArgs,
  CircularLoader,
  GridContainer,
  GridItem,
  isArrayEmpty,
  isEmpty,
  useAsyncCall,
} from "@duosoftbg/nacid-components";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import PreviousSubmittedAppFormSection from "./PreviousSubmittedAppFormSection";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { filterPreviousSubmitted } from "../../../../../../services/seEducationSpecificCalls";
import { Replay } from "@mui/icons-material";

const PreviouSubmittedAppSection = () => {
  const { asyncCall } = useAsyncCall();
  const { t } = useTranslation();
  const [previousSubmittedAppsResponse, setPreviousSubmittedAppsResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const { control } = useFormContext();

  const watchedFields = useWatch({
    control,
    name: [
      "applicant.naturalPerson.personalIdType",
      "applicant.naturalPerson.personalId",
      "applicant.naturalPerson.foreignerIdentifierKind.id",
      "applicant.naturalPerson.foreignerIdentifierCountry.id",
      "applicant.naturalPerson.personalNacidId",
      "applicant.naturalPerson.firstName",
      "applicant.naturalPerson.middleName",
      "applicant.naturalPerson.lastName",
      "applicant.naturalPerson.birthCountry.id",
      "applicant.naturalPerson.dateOfBirth",
    ],
  });

  const { dirtyFields } = useFormState({
    control,
    name: ["applicant.naturalPerson"],
  });

  useEffect(() => {
    const isDirtyEmpty = Object.keys(dirtyFields).length === 0;
    if (isDirtyEmpty) {
      onReload();
    } else {
      setPreviousSubmittedAppsResponse(null);
    }
  }, [dirtyFields, ...watchedFields]);

  const [
    personalIdType,
    personalId,
    foreignerIdentifierKind,
    foreignerIdentifierCountry,
    personalNacidId,
    firstName,
    secondName,
    lastName,
    originCountryCode,
    birthDate,
  ] = watchedFields;

  const onReload = () => {
    setLoading(true);
    const asyncApplicantSubmit: AsyncCallArgs = {
      withGlobalBackdrop: false,
      promise: filterPreviousSubmitted({
        personalIdType,
        personalId,
        foreignerIdentifierKind,
        foreignerIdentifierCountry,
        personalNacidId,
        extractAll: true,
        firstName,
        secondName,
        lastName,
        originCountryCode,
        birthDate,
      }),
      onSuccess: (response) => {
        setLoading(false);
        setPreviousSubmittedAppsResponse(response?.data);
      },
      onError: () => {
        setLoading(false);
      },
    };
    asyncCall(asyncApplicantSubmit);
  };

  if (loading) {
    return (
      <PreviousSubmittedAppFormSection label={t("t.previous.submitted.apps")}>
        <GridContainer>
          <GridItem sm={12} md={12} lg={12}>
            <CircularLoader />
          </GridItem>
        </GridContainer>
      </PreviousSubmittedAppFormSection>
    );
  }

  if (isEmpty(previousSubmittedAppsResponse)) {
    return (
      <PreviousSubmittedAppFormSection label={t("t.previous.submitted.apps")}>
        <GridContainer>
          <GridItem sm={12} md={12} lg={12}>
            <AlertSpg severity="info">
              <span dangerouslySetInnerHTML={{ __html: t("m.instructions.before.reload") }} />
            </AlertSpg>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem sm={12} md={12} lg={12}>
            <Button variant="contained" endIcon={<Replay />} onClick={onReload} size="small">
              {t("l.btn.reload")}
            </Button>
          </GridItem>
        </GridContainer>
      </PreviousSubmittedAppFormSection>
    );
  }

  if (previousSubmittedAppsResponse.hasWarning) {
    return (
      <PreviousSubmittedAppFormSection label={t("t.previous.submitted.apps")}>
        <GridContainer>
          <GridItem sm={12} md={12} lg={12}>
            <AlertSpg severity="warning">{t(previousSubmittedAppsResponse.warningMessageCode)}</AlertSpg>
          </GridItem>
        </GridContainer>
      </PreviousSubmittedAppFormSection>
    );
  }

  if (isArrayEmpty(previousSubmittedAppsResponse?.applications)) {
    return (
      <PreviousSubmittedAppFormSection label={t("t.previous.submitted.apps")}>
        <GridContainer>
          <GridItem sm={12} md={12} lg={12}>
            <AlertSpg severity="info">{t("m.empty.list")}</AlertSpg>
          </GridItem>
        </GridContainer>
      </PreviousSubmittedAppFormSection>
    );
  }

  return (
    <PreviousSubmittedAppFormSection label={t("t.previous.submitted.apps")}>
      <GridContainer>
        <TableContainer style={{ width: "95%" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: "none" }}>{t("h.entryNumber")}</TableCell>
                <TableCell sx={{ border: "none" }}>{t("h.entryDate")}</TableCell>
                <TableCell sx={{ border: "none" }}>{t("h.status")}</TableCell>
                <TableCell sx={{ border: "none" }}>{t("h.serviceType")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previousSubmittedAppsResponse.applications.map((app) => (
                <TableRow key={app.entryNumber} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableCell sx={{ border: "none" }}>{app.entryNumber}</TableCell>
                  <TableCell sx={{ border: "none" }}>{app.entryDate}</TableCell>
                  <TableCell sx={{ border: "none" }}>{app.statusName}</TableCell>
                  <TableCell sx={{ border: "none" }}>{t(app.serviceName)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </GridContainer>
    </PreviousSubmittedAppFormSection>
  );
};

export default PreviouSubmittedAppSection;
